/**
 * Pricing Configuration
 *
 * Decided to do these as constants here instead of fetching from Stripe API
 * to reduce API calls and complexity. Prices are unlikely to change frequently.
 *
 * These are the base USD prices displayed in the app.
 * Stripe Adaptive Pricing automatically converts these to local currencies
 * at checkout based on the customer's location.
 *
 * @see https://docs.stripe.com/payments/currencies/localize-prices/adaptive-pricing
 */

export const PRICING = {
  monthly: {
    amount: 1.99,
    currency: "USD",
    interval: "month",
    displayPrice: "$1.99",
    displayInterval: "month",
  },
  yearly: {
    amount: 17.99,
    currency: "USD",
    interval: "year",
    displayPrice: "$17.99",
    displayInterval: "year",
  },
} as const;

/**
 * Calculate savings percentage for yearly plan
 */
export function getYearlySavingsPercent(): number {
  const monthlyCost = PRICING.monthly.amount * 12;
  const yearlyCost = PRICING.yearly.amount;
  return Math.round(((monthlyCost - yearlyCost) / monthlyCost) * 100);
}

/**
 * Get localized price interval string
 * @param interval - "monthly" or "yearly"
 * @param t - Translation function from useTranslations("premium")
 * @param locale - Current locale (e.g., "en", "zh-CN", "zh-TW")
 * @returns Formatted price string like "$1.99/month" (English) or "$1.99 USD/年" (Chinese)
 */
export function getDisplayPriceInterval(
  interval: "monthly" | "yearly",
  t: (key: string) => string,
  locale: string = "en"
): string {
  const pricing = interval === "monthly" ? PRICING.monthly : PRICING.yearly;
  const localizedInterval = t(interval === "monthly" ? "perMonth" : "perYear");
  const currency = locale === "en" ? "" : " USD";
  return `${pricing.displayPrice}${currency}/${localizedInterval}`;
}
